import {Button, IconButton} from '@mui/material'
import {ReactNode, useEffect} from 'react'
import {regex, required, SelectInput, TextInput, useTranslate} from 'react-admin'
import {useFormContext, useWatch} from 'react-hook-form'
import {makeStyles} from 'tss-react/mui'

import {CloseIcon, FileIcon, PictureIcon, VideoPlayIcon} from '../../components/icons'
import SubtitledInput from '../../components/SubtitledInput'
import useFileSelection from '../../hooks/useFileSelection'
import useObjectUrlFromFile from '../../hooks/useObjectUrlFromFile'
import {EMOJI_REGEX} from '../../utils/consts'

const TemplateBuilderHeader = () => {
  const translate = useTranslate()
  const {classes: styles} = useStyles({})
  const {setValue} = useFormContext()
  return (
    <div>
      <SubtitledInput
        isEmptyValidationErrorMessageHidden
        className={styles.subtitledInput}
        subtitle=""
        title={translate(
          'resources.chat_message_templates.create.contentCard.header.title'
        )}
      >
        <SelectInput
          choices={HEADERS.map(type => ({
            key: type,
            name: translate(
              `resources.chat_message_templates.create.contentCard.header.types.${type}`
            ),
          }))}
          label=""
          emptyText={translate(
            `resources.chat_message_templates.create.contentCard.header.types.NONE`
          )}
          emptyValue="NONE"
          optionValue="key"
          source="header"
          parse={v => v === 'NONE' ? undefined : v}
          defaultValue="NONE"
        />
      </SubtitledInput>
      <HeadlineHeader />
      <MediaHeader onClose={() => setValue('header', 'NONE')} />
    </div>
  )
}


const MediaHeader = ({onClose}) => {
  const {classes: styles} = useStyles({})
  const translate = useTranslate()
  const {setValue} = useFormContext()
  const {header} = useWatch()
  const {
    files: [file],
    input,
    openFileSelectionDialog,
    setFiles,
  } = useFileSelection({
    canDragAndDrop: false,
    encoding: 'base64',
    mimeTypes: {
      DOCUMENT: ['application/pdf'],
      IMAGE: ['image/jpeg', 'image/png'],
      VIDEO: ['video/mp4'],
    }[header] ?? [],
    multiple: false,
    shouldCheckForWhatsappBusinessApiMaxFileSize: true,
  })
  const imageUrl = useObjectUrlFromFile(file?.file)
  useEffect(() => setValue('headerMediaUrl', imageUrl), [imageUrl, setValue])
  useEffect(() => setFiles([]), [header, setFiles])
  useEffect(() => {
    setValue('headerMediaBase64', file?.encodedContent)
    setValue('headerMediaFilename', file?.filename)
    setValue('headerMediaMimeType', file?.mimeType)
  }, [file, setValue])
  if (!['DOCUMENT', 'IMAGE', 'VIDEO'].includes(header)) return null
  return (
    <div>
      {input as ReactNode}
      <Button
        className={styles.uploadMediaButton}
        onClick={openFileSelectionDialog}
        variant="outlined"
        startIcon={<>
          {imageUrl && (
            <>
              {file?.mimeType.startsWith('image/') && (
                <img className={styles.mediaPreviewIcon} src={imageUrl} />
              )}
              {file?.mimeType.startsWith('application/') && (
                <iframe className={styles.mediaPreviewIcon} src={imageUrl} />
              )}
              {file?.mimeType.startsWith('video/') && (
                <video className={styles.mediaPreviewIcon} src={imageUrl} />
              )}
            </>
          )}
          {!imageUrl && {
            DOCUMENT: <FileIcon />,
            IMAGE: <PictureIcon />,
            VIDEO: <VideoPlayIcon />,
          }[header]}
        </>}
      >
        {imageUrl && file?.filename}
        {!imageUrl && translate(
          'resources.chat_message_templates.create.contentCard.header.media.' +
          `uploadButtonText.${header.toLowerCase()}`
        )}
      </Button>
      {file && <IconButton onClick={onClose}><CloseIcon /></IconButton>}
    </div>
  )
}

const HeadlineHeader = () => {
  const translate = useTranslate()
  const {setValue} = useFormContext()
  const {header} = useWatch()
  useEffect(() => {
    if (header === 'TEXT') return
    setValue('headline', undefined)
  }, [header, setValue])
  if (header !== 'TEXT') return null
  return (
    <TextInput
      fullWidth={true}
      slotProps={{
        htmlInput: {disableUnderline: true, maxLength: 50},
        input: {disableUnderline: true},
      }}
      label={false}
      margin="dense"
      multiline
      name="headline"
      placeholder={
        translate('chatMessageTemplateEditor.templateInput.headlinePlaceholder')
      }
      source="headline"
      validate={[
        required(),
        regex(EMOJI_REGEX, translate('validations.emojisNotAllowed')),
      ]}
      variant="standard"
    />
  )
}

const HEADERS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']

const useStyles = makeStyles<any>()(theme => ({
  mediaPreviewIcon: {
    height: theme.remSpacing(3),
    width: theme.remSpacing(3),
  },
  subtitledInput: {
    marginBottom: theme.remSpacing(1),
  },
  uploadMediaButton: {
    margin: 0,
    marginRight: theme.remSpacing(0.5),
  },
}))

export default TemplateBuilderHeader
